import React from "react";
import { motion } from "framer-motion";

const sections = [
  {
    id: "general",
    label: "General",
    faqs: [
      {
        q: "What is Calyro?",
        a: "Calyro is an AI-powered receptionist that answers your calls, understands what the caller wants, and either books an appointment, sends them to sales, or starts a follow-up workflow – 24/7, without you hiring more staff.",
      },
      {
        q: "Who is Calyro for?",
        a: "Calyro is built for service businesses that live and die by phone calls – clinics (dental, med spa, chiropractic, therapy), home services (HVAC, plumbing, electrical), salons & spas, auto services, and any business where a missed call means missed revenue.",
      },
      {
        q: "Is this just another chatbot or IVR menu?",
        a: "No. We don't do 'Press 1 for this, press 2 for that' or generic chatbots. Calyro listens to what the caller says in natural language, classifies intent, applies your business rules, and then takes a clear next action – book, route, or log the call.",
      },
      {
        q: "Does Calyro replace my staff?",
        a: "No. Calyro acts as a front-line filter and backup receptionist. It catches calls your team can't answer, handles repetitive questions and simple bookings, and escalates complex or sensitive cases to a human when needed.",
      },
    ],
  },
  {
    id: "setup",
    label: "Setup & Onboarding",
    faqs: [
      {
        q: "How long does it take to get started?",
        a: "Most customers are live within 24–48 hours. We intentionally avoid instant setups because poor call quality costs real revenue. We custom-build every AI agent to match your business rules, booking logic, and tone — ensuring human-like calls from day one, not rushed, robotic conversations.",
      },
      {
        q: "What do you need from me to set it up?",
        a: "We need your basic business profile: name, location, business hours, services and approximate durations, how you like appointments scheduled (buffers, breaks, new vs existing clients), and where to send bookings and leads (calendar, email, CRM).",
      },
      {
        q: "Do I need to change my phone provider?",
        a: "No. You can either forward your existing business number to a dedicated Calyro number, or, where supported, point calls to Calyro directly from your provider. You stay in control of how calls are routed.",
      },
      {
        q: "Can I test it before real customers talk to it?",
        a: "Yes, and you should. You can call your own Calyro number, try different scenarios (booking, rescheduling, weird questions), see how it behaves, and tweak rules before you forward your real business number.",
      },
    ],
  },
  {
    id: "calls",
    label: "Calls & Numbers",
    faqs: [
      {
        q: "Who actually picks up the call?",
        a: "Calyro is an AI voice agent. It answers with a natural-sounding voice, listens to what the caller says, understands intent, and then follows your rules to book, route, or log the call. No call center, no humans pretending to be AI.",
      },
      {
        q: "Can Calyro transfer calls to my team?",
        a: "Yes. You can define which call types should always go to a human, which hours should be human vs AI, and which numbers to ring for handoff. Calyro will help where it can and transfer when it should.",
      },
      {
        q: "What happens if Calyro doesn't understand the caller?",
        a: "If Calyro isn't sure, it asks a simple clarification question. If it's still unclear, it politely says it will pass the message to your team and logs the call as 'needs review' in your dashboard. No guessing and no fake confidence.",
      },
    ],
  },
  {
    id: "booking",
    label: "Booking & Rules",
    faqs: [
      {
        q: "How does Calyro handle appointment scheduling?",
        a: "Every slot Calyro offers is validated against your business hours, blackout dates, buffers, provider schedules, and service duration. If a time doesn't meet your rules, it's not offered. Your rules, your calendar, no random guesses.",
      },
      {
        q: "Can it handle multiple locations or providers?",
        a: "Yes. You can define multiple locations, providers tied to each location, which services each provider can perform, and separate schedules per provider. Calyro respects all of that when it proposes appointment times.",
      },
      {
        q: "What about urgent or emergency calls?",
        a: "You define what 'urgent' means for your business. Calyro asks the right questions, tags the call as urgent, and either books the earliest slot, routes to a human, or logs a high-priority lead based on your playbook.",
      },
    ],
  },
  {
    id: "ai",
    label: "AI Behavior & Control",
    faqs: [
      {
        q: "Will the AI hallucinate or make things up?",
        a: "The core logic lives in our backend, not only inside a model. We hard-code rules for hours, pricing ranges, services, and policies, and instruct the AI not to invent details. If something isn't defined, Calyro says it doesn't know or logs it for your team.",
      },
      {
        q: "Can I control what the AI is allowed to say?",
        a: "Yes. You control allowed services, topics, disclaimers, and wording. You can also define what it must never promise, like medical advice, discounts, or guarantees.",
      },
      {
        q: "What languages does Calyro support?",
        a: "We start with English as the primary language. Additional languages or locales can be added based on demand.",
      },
    ],
  },
  {
    id: "pricing",
    label: "Pricing & Billing",
    faqs: [
      {
        q: "How is Calyro priced?",
        a: "We offer three plans: The Receptionist ($199/mo), The Front Desk ($349/mo), and Regional HQ ($699/mo). Each includes a set number of minutes. Yearly billing saves you 2 months. Setup fees are mandatory and one-time.",
      },
      {
        q: "Why is there a setup fee?",
        a: "The setup fee ($299–$999 depending on plan) covers white-glove onboarding: custom call flows, booking logic configuration, voice tuning, and QA testing. We don't launch until your agent sounds human and follows your rules. This is a one-time fee, regardless of billing cycle.",
      },
      {
        q: "What happens if I go over my included minutes?",
        a: "You're billed per extra minute at your plan's overage rate ($0.40–$0.50/min). We send warnings at 70%, 90%, and 100% usage so you're never surprised. You can upgrade anytime.",
      },
      {
        q: "Do I have to sign a long-term contract?",
        a: "No. Monthly plans are month-to-month with no commitment. Annual billing saves you 2 months but is optional. You can cancel anytime.",
      },
    ],
  },
  {
    id: "data",
    label: "Integrations & Data",
    faqs: [
      {
        q: "What does Calyro integrate with?",
        a: "Calyro is built to connect with calendars, CRMs, and SMS/email providers. We start with simple webhook/API-based integrations and expand based on the tools our customers actually use.",
      },
      {
        q: "Can I export my data?",
        a: "Yes. Your calls, leads, bookings, and notes belong to you. You can view and filter them in the dashboard, export them, or pull them via API into your own tools.",
      },
      {
        q: "How do you handle privacy and security?",
        a: "We don't sell your data. Calls and logs are stored securely with access restricted to your team and our systems for operating the product. Formal details live in our Privacy Policy and Data Processing pages.",
      },
    ],
  },
  {
    id: "results",
    label: "Results & ROI",
    faqs: [
      {
        q: "What kind of results can I expect?",
        a: "We don't promise fake numbers. Calyro is designed to reduce missed calls, turn more callers into bookings or leads, and give you visibility into what's working. Your revenue depends on your business; we make sure fewer opportunities leak out.",
      },
      {
        q: "How do I know if Calyro is worth it for my business?",
        a: "If even 2–3 extra bookings per month cover the subscription cost and you're currently missing calls or relying on voicemails, Calyro is probably worth it. The dashboard shows handled calls, bookings, and lead volume so you can judge ROI yourself.",
      },
    ],
  },
  {
    id: "support",
    label: "Support & Changes",
    faqs: [
      {
        q: "What if I want to change my rules later?",
        a: "You can update your hours, services, buffers, and handoff rules at any time without rebuilding from scratch. Changes can take effect quickly.",
      },
      {
        q: "What if something goes wrong during a call?",
        a: "If there's a technical issue, the system logs it, marks the call for review, and our team can help debug configuration or bugs. We aim to fail-safe: better to log and escalate than to give incorrect information.",
      },
    ],
  },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export function FAQPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top gradient strip */}
      <div className="h-1 w-full bg-gradient-to-r from-sky-500 via-violet-500 to-fuchsia-500" />

      <main className="mx-auto max-w-6xl px-4 pb-20 pt-10 sm:px-6 lg:px-8">
        {/* Header */}
        <section className="mb-10 text-center">
          <motion.span 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-4 py-1 text-xs font-medium text-slate-600 shadow-xs backdrop-blur"
          >
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse mr-2" />
            FAQs · Calyro.ai
          </motion.span>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
          >
            Answers before you even ask.
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mx-auto mt-4 max-w-2xl text-sm text-slate-600 sm:text-base leading-relaxed"
          >
            Here's exactly what Calyro does, how it behaves, and what you can expect
            in plain language—no hand-wavy promises, no hidden catches.
          </motion.p>
        </section>

        <div className="grid gap-8 lg:grid-cols-[260px,1fr]">
          {/* Left column – section nav */}
          <motion.aside 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-4"
          >
            <div className="rounded-2xl bg-white/80 p-4 shadow-lg ring-1 ring-slate-200 backdrop-blur border border-slate-200">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Browse by topic
              </p>
              <nav className="space-y-1 text-sm">
                {sections.map((section, idx) => (
                  <motion.a
                    key={section.id}
                    href={`#${section.id}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + idx * 0.05 }}
                    whileHover={{ x: 4 }}
                    className="flex items-center justify-between rounded-xl px-3 py-2 text-left text-slate-700 font-medium transition hover:bg-gradient-to-r hover:from-sky-50 hover:to-violet-50 hover:text-slate-900"
                  >
                    <span>{section.label}</span>
                    <span className="text-[10px] font-semibold text-slate-400">
                      {section.faqs.length}
                    </span>
                  </motion.a>
                ))}
              </nav>
            </div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="hidden rounded-2xl bg-gradient-to-br from-sky-50 via-violet-50 to-fuchsia-50 border border-slate-200 shadow-lg lg:block overflow-hidden relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-sky-500/5 via-violet-500/5 to-fuchsia-500/5" />
              <div className="relative h-full w-full px-4 py-5 text-left">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Still unsure?
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-900 leading-snug">
                  Hop on a quick call and see how Calyro handles real conversations.
                </p>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-4 w-full rounded-xl bg-gradient-to-r from-sky-500 to-indigo-500 px-3 py-2.5 text-xs font-bold text-white shadow-md hover:shadow-lg transition-shadow"
                >
                  Talk to sales
                </motion.button>
              </div>
            </motion.div>
          </motion.aside>

          {/* Right column – FAQ sections */}
          <section className="space-y-10">
            {sections.map((section, sectionIdx) => (
              <motion.div 
                key={section.id} 
                id={section.id} 
                className="scroll-mt-24"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: sectionIdx * 0.1 }}
              >
                <div className="mb-4 flex items-center justify-between gap-4">
                  <h2 className="text-lg font-bold text-slate-900 sm:text-xl">
                    {section.label}
                  </h2>
                  <div className="hidden h-px flex-1 bg-gradient-to-r from-sky-200 via-violet-200 to-transparent sm:block" />
                </div>

                <motion.div 
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="space-y-3"
                >
                  {section.faqs.map((faq, idx) => (
                    <motion.details
                      key={idx}
                      variants={fadeInUp}
                      whileHover={{ scale: 1.01, y: -2 }}
                      transition={{ duration: 0.2 }}
                      className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-lg hover:shadow-xl hover:border-blue-300 backdrop-blur transition-all"
                    >
                      <summary className="flex cursor-pointer list-none items-start justify-between gap-3">
                        <span className="text-sm font-bold text-slate-900">
                          {faq.q}
                        </span>
                        <motion.span 
                          className="mt-1 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-[10px] font-bold text-slate-600 transition group-open:rotate-45 group-open:border-sky-300 group-open:bg-gradient-to-r group-open:from-sky-50 group-open:to-violet-50 group-open:text-sky-600"
                          whileHover={{ scale: 1.1 }}
                        >
                          +
                        </motion.span>
                      </summary>
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="mt-3 pr-2 text-sm leading-relaxed text-slate-600"
                      >
                        {faq.a}
                      </motion.div>
                    </motion.details>
                  ))}
                </motion.div>
              </motion.div>
            ))}
          </section>
        </div>
      </main>
    </div>
  );
}

export default FAQPage;
